import React from 'react';
import PropTypes from 'prop-types';
import { ThemeConsumer } from '@cvent/carina-theme-provider';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { carinaFontReset } from './util/styleHelper';
import { ChevronLeftIcon, ChevronRightIcon } from '@cvent/carina-icon';

/**
 * Stateless themeable element for use as child of Pagination
 */
export default class ItemElement extends React.Component {
  static propTypes = {
    /** aria-label or accessibilityLabel attribute */
    accessibilityLabel: PropTypes.string,
    /** aria-hidden or accessible attribute */
    accessible: PropTypes.bool,
    /** Disabled attribute */
    disabled: PropTypes.bool,
    /** Icon name */
    icon: PropTypes.string,
    /** Text direction */
    isRtl: PropTypes.bool,
    /** Text to display inside the page button */
    text: PropTypes.string,
    /** Interaction event handler */
    onPress: PropTypes.func,
    /** Selected page */
    selected: PropTypes.bool,
    /** For e2e tests (maps to `data-cvent-id`) */
    testID: PropTypes.string
  };

  render() {
    const {
      accessibilityLabel,
      accessible,
      isRtl,
      testID,
      text,
      disabled,
      selected,
      onPress,
      icon
    } = this.props;

    return (
      <ThemeConsumer>
        {theme => (
          <button
            type="button"
            aria-hidden={!accessible}
            aria-label={accessibilityLabel}
            disabled={disabled || selected}
            css={getPageStyles({ theme, isRtl, selected })}
            {...injectTestId(testID)}
            tabIndex={selected ? '-1' : 0}
            onClick={onPress}
          >
            {this.renderIcon(icon, theme, disabled)}
            {text}
          </button>
        )}
      </ThemeConsumer>
    );
  }

  renderIcon(icon, theme, disabled) {
    if (icon === 'left') {
      return (
        <ChevronLeftIcon
          size="l"
          color={disabled ? theme.font.color.disabled : theme.font.color.soft}
        />
      );
    } else if (icon === 'right') {
      return (
        <ChevronRightIcon
          size="l"
          color={disabled ? theme.font.color.disabled : theme.font.color.soft}
        />
      );
    }
  }
}

function getPageStyles({ theme, isRtl, selected }) {
  return {
    ...carinaFontReset(theme),
    appearance: 'none',
    lineHeight: 1,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    background: 'none',
    backgroundColor: 'transparent',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: theme.size.border.radius.twoX,
    borderColor: 'transparent',
    cursor: 'pointer',
    outline: 'none',
    fontSize: theme.font.base.size.s,
    color: theme.font.color.interactive.base,
    direction: isRtl ? 'rtl' : 'ltr',
    textAlign: 'center',
    margin: 0,
    padding: 8,
    zIndex: 0,
    minWidth: 16,
    position: 'relative',
    svg: {
      margin: '-8px -5px',
      transform: isRtl ? 'rotate(-180deg)' : 'none'
    },
    '&:hover': {
      color: theme.font.color.interactive.hover,
      backgroundColor: theme.backgroundColor.interactive.hover
    },
    '&:active': {
      color: theme.font.color.interactive.active,
      backgroundColor: theme.backgroundColor.interactive.active
    },
    '&:focus': {
      boxShadow: `
        ${theme.depth.boxShadow.soft.xOffset}
        ${theme.depth.boxShadow.soft.yOffset}
        ${theme.depth.boxShadow.soft.blurRadius}
        ${theme.depth.boxShadow.soft.spreadRadius}
        ${theme.depth.boxShadow.soft.color},
        0 0 0 3px ${theme.borderColor.interactive.focus}
      `,
      zIndex: 1
    },
    '&:disabled': {
      color: theme.font.color.disabled,
      fill: theme.font.color.disabled,
      pointerEvents: 'none'
    },
    '&::-moz-focus-inner': {
      border: 0,
      padding: 0
    },
    ...getCurrentPageStyles(theme, selected)
  };
}

function getCurrentPageStyles(theme, selected) {
  if (selected) {
    return {
      borderRadius: 0,
      color: theme.font.color.base,
      pointerEvents: 'none',
      '&::before': {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        content: '""',
        height: 4,
        backgroundColor: theme.backgroundColor.accent.active
      },
      '&:hover': {
        backgroundColor: 'transparent'
      },
      '&:focus': {
        outline: 'none'
      },
      '&:disabled': {
        color: theme.font.color.disabled
      }
    };
  }
}
